import React, { useEffect, useMemo, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import {
  captionToTipTapHtml,
  serializeCaptionEditorHtml,
} from '../lib/carousel_caption_html.js';
import { getCarouselCaptionEditorExtensions } from '../lib/carousel_caption_editor_extensions.js';
import { useTiptapActiveEditor } from './TiptapActiveEditorContext.jsx';

function isToolbarOrCaptionFocus(el) {
  if (!el || !(el instanceof Element)) return false;
  return !!(
    el.closest('.tiptap-toolbar') ||
    el.closest('.ps-toolbar-menu') ||
    el.closest('.ps-toolbar-popover-anchor') ||
    el.closest('[data-carousel-caption-editor]')
  );
}

/**
 * Single-slide rich caption: shares the post toolbar via TiptapActiveEditorContext.
 */
export function CarouselCaptionEditor({ captionHtml, onCaptionChange, editable = true }) {
  const overlay = useTiptapActiveEditor();
  const extensions = useMemo(() => getCarouselCaptionEditorExtensions(), []);
  // Last value the editor itself emitted via onUpdate. Used to detect
  // external prop changes (e.g. main-editor undo of slides attr) so we can
  // reseed the caption editor; without this the editor sticks at whatever
  // content it had before the undo and silently drifts from slide.caption.
  const lastEmittedRef = useRef(captionHtml ?? '');

  const editor = useEditor(
    {
      extensions,
      content: captionToTipTapHtml(captionHtml),
      editable,
      onUpdate: ({ editor: ed }) => {
        const next = serializeCaptionEditorHtml(ed);
        lastEmittedRef.current = next;
        onCaptionChange(next);
      },
      editorProps: {
        attributes: {
          class: 'post-content-carousel__caption-editor',
          'data-carousel-caption-editor': '1',
          spellcheck: 'true',
        },
      },
    },
    [extensions, editable]
  );

  // Sync caption editor content when `captionHtml` changes externally (i.e.
  // the new value did not originate from this editor's own onUpdate). This
  // covers main-editor undo/redo of the slides attribute. Skip when the
  // prop matches the last value we emitted to avoid re-seeding mid-typing.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    const incoming = captionHtml ?? '';
    if (incoming === lastEmittedRef.current) return;
    const current = serializeCaptionEditorHtml(editor);
    if (current === incoming) {
      lastEmittedRef.current = incoming;
      return;
    }
    lastEmittedRef.current = incoming;
    editor.commands.setContent(captionToTipTapHtml(incoming), false);
  }, [editor, captionHtml]);

  useEffect(() => {
    if (!editor || !overlay) return;

    const onFocus = () => {
      overlay.setActiveOverlayEditor(editor);
    };

    const onBlur = () => {
      requestAnimationFrame(() => {
        const ae = document.activeElement;
        if (isToolbarOrCaptionFocus(ae)) return;
        overlay.setActiveOverlayEditor(null);
      });
    };

    editor.on('focus', onFocus);
    editor.on('blur', onBlur);
    return () => {
      editor.off('focus', onFocus);
      editor.off('blur', onBlur);
    };
  }, [editor, overlay]);

  if (!editor) return null;

  return <EditorContent editor={editor} />;
}
